import { useMemoizedFn, useUpdate } from 'ahooks';
import queryString from 'query-string';
import type { ParseOptions, StringifyOptions } from 'query-string';
import { useMemo, useRef } from 'react';
import type * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Options {
  navigateMode?: 'push' | 'replace';
  parseOptions?: ParseOptions;
  stringifyOptions?: StringifyOptions;
}

const baseParseConfig: ParseOptions = {
  parseNumbers: false,
  parseBooleans: false,
};

const baseStringifyConfig: StringifyOptions = {
  skipNull: false,
  skipEmptyString: false,
};

type UrlState = Record<string, any>;

export const useUrlState = <S extends UrlState = UrlState>(
  initialState: S,
  options?: Options & { allowedValues: Record<string, any> }
) => {
  type State = Partial<{ [key in keyof S]: any }>;
  const {
    navigateMode = 'push',
    parseOptions,
    stringifyOptions,
  } = options || {};

  const mergedParseOptions = {
    ...baseParseConfig,
    ...parseOptions,
  };
  const mergedStringifyOptions = {
    ...baseStringifyConfig,
    ...stringifyOptions,
  };

  const location = useLocation();
  const navigate = useNavigate();

  const update = useUpdate();

  const initialStateRef = useRef(initialState || {});

  const queryFromUrl = useMemo(() => {
    return queryString.parse(location.search, mergedParseOptions);
  }, [location.search]);

  const safeQueryFromUrl = useMemo(
    () =>
      options?.allowedValues
        ? Object.fromEntries(
            Object.keys(options.allowedValues).map((key) => [
              key,
              options.allowedValues[key].includes(queryFromUrl[key])
                ? queryFromUrl[key]
                : initialState[key],
            ])
          )
        : {},
    [queryFromUrl]
  );

  const targetQuery: State = useMemo(
    () => ({
      ...initialStateRef.current,
      ...queryFromUrl,
      ...safeQueryFromUrl,
    }),
    [queryFromUrl, safeQueryFromUrl]
  );

  const setState = (s: React.SetStateAction<State>) => {
    const newQuery = typeof s === 'function' ? s(targetQuery) : s;

    update();

    navigate(
      {
        hash: location.hash,
        search:
          queryString.stringify(
            { ...queryFromUrl, ...newQuery },
            mergedStringifyOptions
          ) || '?',
      },
      {
        replace: navigateMode === 'replace',
        state: location.state,
      }
    );
  };

  return [targetQuery, useMemoizedFn(setState)] as const;
};
